import type { ChatterLevel } from '@chessmate/persona';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The one audio settings store (spec §Settings — `docs/proposals/ai-personas-voice-and-sound.md`):
 * master/sfx/music/voice volumes, mute, chatter level, narrator on/off, voices on/off, the
 * sensory-friendly preset — and the parent locks.
 *
 * It replaces `voice-settings-store.ts` (whose keys are migrated in, see `readLegacyAudio`) and
 * takes the chatter level over from `persona-store.ts`, so there is ONE place that says what this
 * device sounds like.
 *
 * What the app actually plays is the EFFECTIVE settings (`effectiveAudio`), never the raw ones.
 * A parent lock is enforced in the effective value, so a locked setting cannot be changed from the
 * game — not by the settings screen and not by a code path that forgets to check the lock.
 */

/** The locks a grown-up sets for the whole device. */
export interface ParentLocks {
  /** Voices off: no narration, no speaker button, nothing spoken on this device. */
  readonly voices: boolean;
  /** Chatter off: the personas stay quiet. */
  readonly chatter: boolean;
  /** Music off: music cannot be turned on. */
  readonly music: boolean;
  /** The loudest this device may be (the master bus), or null for no cap. */
  readonly volumeCap: number | null;
}

export const NO_LOCKS: ParentLocks = { voices: false, chatter: false, music: false, volumeCap: null };

/**
 * The settings as DATA — no setters.
 *
 * The pure helpers (`effectiveAudio`, `applyAudioSettings`) read only this, so they can be handed
 * a plain object in a test instead of a whole store.
 */
export interface AudioSettingsData {
  /** Overall level (the master bus). 0..1. */
  readonly masterVolume: number;
  /** Sound effects. 0..1. */
  readonly sfxVolume: number;
  /** Music. 0..1. */
  readonly musicVolume: number;
  /** Spoken words (lessons, narration). 0..1. */
  readonly voiceVolume: number;
  /** Mute everything, whatever the volumes say. */
  readonly muted: boolean;
  readonly sfxOn: boolean;
  readonly musicOn: boolean;
  /** The one-time music offer was answered (so it never asks again). */
  readonly musicOfferAnswered: boolean;
  /** The sensory-friendly preset: softer sound effects, no music, no chatter. */
  readonly sensory: boolean;
  /** How chatty the personas are. Moved here from the persona store (spec: one audio store). */
  readonly chatter: ChatterLevel;
  /** Voices are available on this device (the speaker button works). */
  readonly voiceOn: boolean;
  /** The app speaks on its own — today's `autoSpeak`. Off by default (ratified 2026-10-15). */
  readonly narratorOn: boolean;
  /** The TTS voice id. Migrated from `voice-settings-store.ts`. */
  readonly voice: string;
  /** The locks a grown-up set for this device. */
  readonly locks: ParentLocks;
}

/** The store's shape: the data plus the ways to change it. */
export interface AudioSettings extends AudioSettingsData {
  setMasterVolume(volume: number): void;
  setSfxVolume(volume: number): void;
  setMusicVolume(volume: number): void;
  setVoiceVolume(volume: number): void;
  setMuted(on: boolean): void;
  setSfxOn(on: boolean): void;
  setMusicOn(on: boolean): void;
  answerMusicOffer(on: boolean): void;
  setSensory(on: boolean): void;
  setChatter(chatter: ChatterLevel): void;
  setVoiceOn(on: boolean): void;
  setNarratorOn(on: boolean): void;
  setVoice(voice: string): void;
  /** Grown-up controls (behind the maths gate on the settings screen). */
  lockVoices(on: boolean): void;
  lockChatter(on: boolean): void;
  lockMusic(on: boolean): void;
  setVolumeCap(cap: number | null): void;
  clearLocks(): void;
}

/** What the device actually plays: the settings after the locks and the preset are applied. */
export interface EffectiveAudio {
  readonly master: number;
  readonly sfx: number;
  readonly music: number;
  readonly voice: number;
  readonly muted: boolean;
  readonly sensory: boolean;
  readonly chatter: ChatterLevel;
  readonly voiceOn: boolean;
  readonly narratorOn: boolean;
}

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/**
 * The effective settings: the raw settings with the parent locks, the volume cap and the
 * sensory-friendly preset applied.
 *
 * The cap rides on the MASTER bus, which the mixer multiplies into every other bus — so a cap of
 * 0.5 really is "nothing louder than half", not just "the master slider at half".
 */
export function effectiveAudio(s: AudioSettingsData): EffectiveAudio {
  const cap = s.locks.volumeCap;
  const voiceOn = s.voiceOn && !s.locks.voices;
  return {
    master: Math.min(s.masterVolume, cap ?? 1),
    sfx: s.sfxOn ? s.sfxVolume : 0,
    // The sensory-friendly preset says "no music", so the music bus is silent under it — the
    // mixer already zeroes that bus, and the effective value says the same thing.
    music: s.musicOn && !s.locks.music && !s.sensory ? s.musicVolume : 0,
    voice: voiceOn ? s.voiceVolume : 0,
    muted: s.muted,
    sensory: s.sensory,
    // The sensory-friendly preset is quiet all round: no music, no chatter.
    chatter: s.locks.chatter || s.sensory ? 'off' : s.chatter,
    voiceOn,
    // The narrator is silenced by the LOCK, not by the child's own voice switch: the switch says
    // "read the lesson aloud", and it keeps its state when the voices are turned off (the speech
    // itself stops, because there is no voice to play it).
    narratorOn: s.narratorOn && !s.locks.voices,
  };
}

/**
 * The settings as they were before this store existed: `voice-settings-store.ts` kept its keys
 * under `s4k:voice`, and the persona store kept the chatter level under `s4k:persona`. Both are
 * read once, here, so an existing device keeps its voice and its narrator choice.
 */
export interface LegacyAudioSettings {
  readonly voiceOn?: boolean;
  readonly narratorOn?: boolean;
  readonly voice?: string;
  readonly chatter?: ChatterLevel;
}

const LEGACY_VOICE_KEY = 's4k:voice';
const PERSONA_KEY = 's4k:persona';

function readPersistedState(storage: Storage, key: string): Record<string, unknown> | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
    return parsed.state ?? null;
  } catch {
    return null;
  }
}

/** The legacy keys, read from storage. Pure enough to test; never throws. */
export function readLegacyAudio(storage: Storage | undefined): LegacyAudioSettings {
  if (!storage) return {};
  const voice = readPersistedState(storage, LEGACY_VOICE_KEY);
  const persona = readPersistedState(storage, PERSONA_KEY);
  const out: {
    voiceOn?: boolean;
    narratorOn?: boolean;
    voice?: string;
    chatter?: ChatterLevel;
  } = {};
  if (typeof voice?.voiceOn === 'boolean') out.voiceOn = voice.voiceOn;
  if (typeof voice?.autoSpeak === 'boolean') out.narratorOn = voice.autoSpeak;
  if (typeof voice?.voice === 'string') out.voice = voice.voice;
  if (persona?.chatter === 'off' || persona?.chatter === 'little' || persona?.chatter === 'lot') {
    out.chatter = persona.chatter;
  }
  return out;
}

function storageOrUndefined(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

const legacy = readLegacyAudio(storageOrUndefined());

export const useAudioSettingsStore = create<AudioSettings>()(
  persist(
    (set, get) => ({
      masterVolume: 1,
      sfxVolume: 0.8,
      musicVolume: 0.5,
      voiceVolume: 0.9,
      muted: false,
      sfxOn: true,
      musicOn: false,
      musicOfferAnswered: false,
      sensory: false,
      chatter: legacy.chatter ?? 'little',
      voiceOn: legacy.voiceOn ?? true,
      narratorOn: legacy.narratorOn ?? false,
      voice: legacy.voice ?? 'en-US-AriaNeural',
      locks: NO_LOCKS,

      setMasterVolume(volume) {
        // A cap is a ceiling, not a suggestion: the wish is clamped as it is set.
        const cap = get().locks.volumeCap;
        set({ masterVolume: clamp01(cap === null ? volume : Math.min(volume, cap)) });
      },
      setSfxVolume(volume) {
        set({ sfxVolume: clamp01(volume) });
      },
      setMusicVolume(volume) {
        set({ musicVolume: clamp01(volume) });
      },
      setVoiceVolume(volume) {
        set({ voiceVolume: clamp01(volume) });
      },
      setMuted(on) {
        set({ muted: on });
      },
      setSfxOn(on) {
        set({ sfxOn: on });
      },
      setMusicOn(on) {
        // Locked music stays off, whatever the game asks for.
        if (get().locks.music) return;
        set({ musicOn: on, musicOfferAnswered: true });
      },
      answerMusicOffer(on) {
        if (get().locks.music) {
          set({ musicOfferAnswered: true });
          return;
        }
        set({ musicOn: on, musicOfferAnswered: true });
      },
      setSensory(on) {
        set({ sensory: on });
      },
      setChatter(chatter) {
        if (get().locks.chatter) return;
        set({ chatter });
      },
      setVoiceOn(on) {
        if (get().locks.voices) return;
        set({ voiceOn: on });
      },
      setNarratorOn(on) {
        if (get().locks.voices) return;
        set({ narratorOn: on });
      },
      setVoice(voice) {
        set({ voice });
      },
      lockVoices(on) {
        set({ locks: { ...get().locks, voices: on } });
      },
      lockChatter(on) {
        set({ locks: { ...get().locks, chatter: on } });
      },
      lockMusic(on) {
        set({ locks: { ...get().locks, music: on } });
      },
      setVolumeCap(cap) {
        const next = cap === null ? null : clamp01(cap);
        const locks = { ...get().locks, volumeCap: next };
        // Clamp the stored level too, so lifting the cap later does not surprise the child with a
        // louder setting than the one they were last allowed to choose.
        const masterVolume =
          next === null ? get().masterVolume : Math.min(get().masterVolume, next);
        set({ locks, masterVolume });
      },
      clearLocks() {
        set({ locks: NO_LOCKS });
      },
    }),
    { name: 's4k:audio' },
  ),
);

// The legacy blob has been read (above) and its values are in the store's defaults. Write them
// out once, then drop the old key: from here on the audio store is the only source. The write is
// what persists the migrated values — a device that never changes a setting must not lose them on
// the next reload.
const storage = storageOrUndefined();
if (storage?.getItem(LEGACY_VOICE_KEY)) {
  useAudioSettingsStore.setState({});
  storage.removeItem(LEGACY_VOICE_KEY);
}
